import { clone, isArray, isNumber, orderBy, unionBy, debounce, uniqWith, isBoolean, sortBy } from 'lodash';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  mergeWith,
  of,
  scan,
  startWith,
  Subject,
  switchMap
} from 'rxjs';

import {
  CommentIssue,
  IssueCommentReply,
  IssueCommentWSMessageResponse
} from 'common-module/src/components/Epic/Model/comment';
import { Member } from 'common-module/src/components/Epic/Model/member';
import { unreadCommentItem } from 'liga-detail-en/src/models/issue';

import { usePromise } from 'liga-detail-en/src/utils/usePromise';
import { useSocket } from 'liga-detail-en/src/utils/useSocket';
import { useVuex } from 'liga-detail-en/src/utils/useVuex';
import { fromRef } from 'common-module/src/util/fromRef';
import { computed, nextTick, reactive, ref, Ref, watch } from '@vue/composition-api';
import { useObservable } from '@vueuse/rxjs';

import { getCommentList, markCommentIdMessageRead, queryUnReadComment, recordLastViewCommentTime } from 'liga-detail-en/src/api/comment';
import { ActionStore, IssueDrawerActionEdit } from './action';
import { FieldsStore } from './fields';
import { IssueStore } from './issue';
import { LayoutStore } from './layout';
import { until } from '@vueuse/core';
import axios, { CancelTokenSource } from 'axios';

let commentRequestCancel: CancelTokenSource | undefined;

export const useComment = (
  actionStore: ActionStore,
  issueStore: IssueStore,
  fieldStore: FieldsStore,
  layoutStore: LayoutStore,
  isVisitor: Ref<boolean>
) => {
  const vuex = useVuex();
  const socket = useSocket();
  const order = ref<'asc' | 'desc'>('asc');
  const commentLoading = ref(true);
  // 正在计时的未读评论
  const inProgressUnreadComment = ref<number[]>([]);

  const showBubble = reactive<{
    show: boolean;
    comment: CommentIssue | IssueCommentReply | undefined;
    comments:(CommentIssue | IssueCommentReply)[];
      }>({
        show: false,
        comment: undefined,
        comments: []
      });


  actionStore.onClose(({ action }) => {
    if (action?.type === 'Edit' && !isVisitor.value) {
      socket.emit('leave', `ISSUE_${action.issueId}`);
    }
    commentRequestCancel?.cancel();
    commentLoading.value = true;
    lastReceiveCommentIds.value = [];
    handleResetUnreadComment();
    unreadCommentList.value = [];
    resetShowBubble();
    if (originCommentResponse.value) {
      originCommentResponse.value = null;
    }
  });

  watch([actionStore.action, issueStore.issueAssemble], async ([action, issue]) => {
    if (
      action?.type === 'Edit' &&
      !isVisitor.value &&
      issue?.issueVO?.projectId
    ) {
      socket.emit('join', `ISSUE_${action.issueId}`);
      commentLoading.value = true;
      refresh(action.issueId, issue.issueVO.projectId).then(() => {
        commentLoading.value = false;
      });
      //记录未读消息评论
      if (issueStore.issueAssemble.value?.unreadMsgComments) {
        const { show, comment, comments } = await judgeShowBubbleByActionUnreadComment();
        if (show) {
          console.log('show', show, comments);
          showBubble.show = true;
          showBubble.comment = comment;
          showBubble.comments = comments;
        }
        getUnreadMsgComment((issueStore.issueAssemble.value?.unreadMsgComments || []).reverse(), true);
      }
      //记录未读评论
      unreadCommentList.value = (issueStore.issueAssemble.value?.unReadComments || []);
      lastViewCommentTime.value = Date.now();
    }
  });

  const localMessages = new Subject<IssueCommentWSMessageResponse>();
  const onReceiveCommentLocalMessage = (msg: IssueCommentWSMessageResponse) => {
    if (actionStore.action.value?.type === 'Edit') {
      msg.result.linkId = actionStore.action.value.issueId;
      msg.local = true;
      localMessages.next(msg);
    }
  };

  const receiveCommentMessages = useObservable(
    combineLatest([
      fromRef(actionStore.action)
    ]).pipe(
      switchMap(([action]) => {
        if (action?.type === 'Edit' && !isVisitor.value) {
          return socket
            .on$<IssueCommentWSMessageResponse>('global:comment')
            .pipe(
              mergeWith(localMessages.asObservable()),
              filter((msg) => {
                return !!(
                  msg?.result &&
                  ['Issue.comment'].includes(msg.msgType) &&
                  ['reply', 'add', 'update', 'delete'].includes(msg.action)
                );
              }),
              filter((msg) => {
                return (
                  msg.action === 'delete' || // 删除消息没有 linkId
                  msg.result.linkId === action.issueId
                );
              }),
              distinctUntilChanged((a, b) => {
                if (a.action === 'update' || b.action === 'update') {
                  return false;
                }
                if (a.action === b.action) {
                  return a.result.commentId === b.result.commentId;
                }

                return false;
              }),
              scan((acc, res) => {
                if (res) {
                  if (!res.result.deleteFlag) {
                    res.result.deleteFlag = 0;
                  }
                  if (!res.result.stickerVOList) {
                    res.result.stickerVOList = [];
                  }
                  if (!res.result.favourUserList) {
                    res.result.favourUserList = [];
                  }
                  if (!res.result.files) {
                    res.result.files = [];
                  }
                  if (!res.result.subReplies) {
                    res.result.subReplies = [];
                  }

                  res.result.stickerVOList.forEach((sticker) => {
                    if (!Reflect.has(sticker, 'containsSelf')) {
                      sticker.containsSelf = sticker.replyUserList.some(
                        (u) => u.id === vuex.state.user.account.userId
                      );
                    }
                  });

                  if (!Reflect.has(res.result, 'favour')) {
                    res.result.favour = res.result.favourUserList.some(
                      (u) => u.id === vuex.state.user.account.userId
                    )
                      ? 1
                      : 0;
                  }

                  // 去重
                  if (['reply', 'add', 'delete'].includes(res.action)) {
                    const index = acc.findIndex(
                      (c) =>
                        res.result.commentId === c.result.commentId &&
                        res.action === c.action
                    );
                    if (index !== -1) {
                      if (
                        res.result.updateTime > acc[index].result.updateTime
                      ) {
                        acc[index] = res;
                      }
                      return acc;
                    }
                  }

                  acc.push(res);
                }

                return acc;
              }, [] as IssueCommentWSMessageResponse[]),
              startWith([] as IssueCommentWSMessageResponse[])
            );
        }
        return of([]);
      })
    ),
    {
      initialValue: [] as IssueCommentWSMessageResponse[]
    }
  );

  const {
    result: originCommentResponse,
    refresh,
    onReceive: onCommentsReceive
  } = usePromise(
    async (issueId: number, projectId: number) => {
      commentRequestCancel = axios.CancelToken.source();
      return getCommentList(issueId, projectId, 'create_time', 'desc', {
        cancelToken: commentRequestCancel.token
      });
    },
    null,
    { immediate: false, clearOnRefresh: false }
  );

  const compositionComments = computed(() => {
    const addedComments = receiveCommentMessages.value
      .filter((msg) => msg.action === 'add')
      .map((msg) => msg.result);
    const addedReplies = receiveCommentMessages.value
      .filter((msg) => msg.action === 'reply')
      .map((msg) => msg.result as unknown as IssueCommentReply);

    const deleteComment = receiveCommentMessages.value
      .filter((msg) => msg.action === 'delete')
      .map((msg) => msg.result.commentId);

    const modifiedCommentMap = receiveCommentMessages.value.reduce(
      (map, msg) => {
        if (msg.action === 'update') {
          map.set(msg.result.commentId, msg.result);
        }
        return map;
      },
      new Map<number, CommentIssue>()
    );

    const commentIdRecord: number[] = [];

    return orderBy(
      [...addedComments, ...(originCommentResponse.value?.list || [])]
        .reduce((arr, comment) => {
          const modifiedComment =
            modifiedCommentMap.get(comment.commentId) || comment;

          // 排序的目的是为了保证回复的回复在回复的后面
          const allReplies = orderBy(
            addedReplies,
            (r) => r.updateTime,
            'asc'
          ).reduce((arr, reply) => {
            if (
              reply.replyId === comment.commentId ||
              arr.some((r) => r.commentId === reply.replyId)
            ) {
              arr.push(reply);
            }
            return arr;
          }, clone(comment.subReplies));

          const subReplies = orderBy(
            allReplies.reduce((subArr, subComment) => {
              const modifiedSubComment =
                modifiedCommentMap.get(subComment.commentId) || subComment;
              if (modifiedSubComment) {
                subArr.push({
                  ...modifiedSubComment
                } as IssueCommentReply);
              }
              return subArr;
            }, [] as IssueCommentReply[]),
            'createTime',
            'asc'
          );

          if (deleteComment.includes(modifiedComment.commentId)) {
            modifiedComment.deleteFlag = 1;
          }
          subReplies.forEach((reply) => {
            if (deleteComment.includes(reply.commentId)) {
              reply.deleteFlag = 1;
            }
          });
          arr.push({
            ...modifiedComment,
            subReplies: uniqWith(subReplies.filter(
              (reply) =>
                reply.deleteFlag === 0 ||
                subReplies.some((r) => r.replyId === reply.commentId)
            ), (a, b) => a.commentId === b.commentId)
          });
          return arr;
        }, [] as CommentIssue[])
        .filter(
          comment => {
            if (commentIdRecord.includes(comment.commentId)) {
              return false;
            }
            commentIdRecord.push(comment.commentId);
            const isAllSubRepliesDeleted = comment.subReplies.length ? comment.subReplies.every(r => r.deleteFlag === 1) : true;
            if (comment.deleteFlag === 1 && isAllSubRepliesDeleted) {
              return false;
            }
            return comment.deleteFlag === 0 || comment.subReplies.length;
          }
        ),
      'createTime',
      order.value
    );
  });

  const MAX_Recommended_COUNT = 5;
  const getRecommendedMembers = (
    commentId?: number,
    skipUserId: number[] = []
  ) => {
    if (
      actionStore.action.value?.type === 'Edit' &&
      issueStore.issueAssemble.value &&
      fieldStore.members.value
    ) {
      const result = new Set<number | null | undefined>();

      unionBy(
        orderBy(
          compositionComments.value.reduce((acc, comment) => {
            if (comment.deleteFlag === 0) {
              acc.push({
                user: comment.commentUser.userId,
                time: comment.updateTime
              });

              comment.commentAttributes?.commentAtInfos?.forEach((m, i) => {
                acc.push({
                  user: m.userId,
                  time: comment.updateTime + i
                });
              });
            }

            comment.subReplies.forEach((reply) => {
              if (reply.deleteFlag === 0) {
                acc.push({
                  user: reply.commentUser.userId,
                  time: reply.updateTime
                });

                reply.commentAttributes?.commentAtInfos?.forEach((m, i) => {
                  acc.push({
                    user: m.userId,
                    time: comment.updateTime + i
                  });
                });
              }
            });

            return acc;
          }, [] as Array<{ user: number; time: number }>),
          'time',
          'desc'
        ).map((c) => c.user)
      )?.forEach((u) => result.add(u));

      result.add(fieldStore.fieldValues.value.assignee);
      result.add(fieldStore.fieldValues.value.owner);
      result.add(issueStore.issueAssemble.value.issueVO.data.createBy);
      result.add(issueStore.issueAssemble.value.issueVO.data.updateBy);

      if (isArray(fieldStore.fieldValues.value.follows)) {
        fieldStore.fieldValues.value.follows?.forEach((id) => {
          result.add(id);
        });
      }

      layoutStore.allFields.value.forEach((field) => {
        if (field.controlCode === 'member') {
          const value = fieldStore.fieldValues.value[field.fieldCode];
          if (value) {
            if (isArray(value)) {
              value.forEach((id) => {
                result.add(id);
              });
            }
            if (isNumber(value)) {
              result.add(value);
            }
          }
        }
      });

      return Array.from(result)
        .map((id) => {
          return id
            ? (fieldStore.members.value || []).find(
              (member) => member.userId === id
            )
            : null;
        })
        .filter((m) => {
          return (
            m &&
            m.enabled &&
            ![...skipUserId, vuex.state.user.account.userId].includes(m.userId)
          );
        })
        .slice(0, MAX_Recommended_COUNT) as Member[];
    }
    return [];
  };

  //a、评论艾特 回复我的消息记录
  const unreadMsgCommentList = ref<unreadCommentItem[]>([]);

  const lastReceiveCommentIds = ref<string[]>([]);
  //1、从issueAssemble中获取评论未读记录
  //2、ws接收到评论消息时，将评论添加到unreadMsgCommentList中
  const getUnreadMsgComment = (unreadComments: unreadCommentItem[], clear = false) => {
    if (clear) {
      unreadMsgCommentList.value = [];
    }
    if (unreadComments?.length) {
      unreadComments.forEach((item: unreadCommentItem) => {
        const unreadCommentIds = unreadMsgCommentList.value.map(
          (item) => item.commentId);
        //如果未读记录中没有该评论，则添加到未读记录中
        if (unreadCommentIds.includes(item.commentId)) {
          //先去除未读评论记录再添加
          unreadMsgCommentList.value.splice(unreadCommentIds.indexOf(item.commentId), 1);
        }
        unreadMsgCommentList.value.unshift(item);
      });
      unreadMsgCommentList.value = [...unreadMsgCommentList.value];
    }
  };

  //判断评论是否与我有关 有关则添加到未读记录中,通过两个条件判断  是否回复我 是否艾特我
  const isRelatedToMe = (judgeComment: (CommentIssue | IssueCommentReply)) => {
    //这里有问题，自己艾特自己的评论 是有消息通知的， 但是自己回复自己是没有的，所以判断逻辑 艾特我 =》 过滤自己评论 =》 回复我 312
    //如果后续 把自己艾特自己的评论也过滤掉了，那么判断逻辑 过滤自己评论 =》 回复我 =》 艾特我 123 或者 132

    //3、判断是否艾特我
    const judgeCommentAtInfos = judgeComment?.commentAttributes?.commentAtInfos;
    if (judgeCommentAtInfos && judgeCommentAtInfos.length) {
      const judgeCommentAtIds = judgeCommentAtInfos.map((item) => item.userId);
      if (judgeCommentAtIds.includes(vuex.state.user.account.userId)) {
        return true;
      }
    }
    //1、先过滤掉我发布的评论
    if (judgeComment.commentUser.userId === vuex.state.user.account.userId) {
      return false;
    }
    //2、判断是否回复我
    //a 找出我发布的评论 b 回复我发布的评论
    const allComments = compositionComments.value.flatMap((comment) => [comment, ...comment.subReplies]);
    const myComments = allComments.filter((comment) => comment.commentUser.userId === vuex.state.user.account.userId);
    const myCommentIds = myComments.map((comment) => comment.commentId);
    const judgeCommentReplyId = (judgeComment as IssueCommentReply)?.replyId;
    if (judgeCommentReplyId && myCommentIds.includes(judgeCommentReplyId)) {
      return true;
    }
    return false;
  };

  watch(() => receiveCommentMessages.value, (newVal) => {
    const newReceiveComments = filterNewReceiveComments(newVal);
    if (newReceiveComments.length) {
      handleMsgUnreadComments(newReceiveComments);
      judgeNewWsShowBubble(newReceiveComments);
      handleUnreadComment(newReceiveComments);
    }
  });

  //处理未读关于我的消息
  const handleMsgUnreadComments = (comments: IssueCommentWSMessageResponse[]) => {
    handleMsgDeleteComments(comments);
    handleMsgUpdateComments(comments);
    handleMsgNewComments(comments);
  };
  //获取新收到的评论
  function filterNewReceiveComments(newVal: IssueCommentWSMessageResponse[]) {
    return newVal.filter((item, index) => {
      const commentId = `${item.action}-${item.result.commentId}-${item.result.updateTime}-${index}`;
      if (lastReceiveCommentIds.value.includes(commentId)) {
        return false;
      }
      lastReceiveCommentIds.value.push(commentId);
      return true;
    });
  }
  //处理删除的评论
  function handleMsgDeleteComments(comments: IssueCommentWSMessageResponse[]) {
    const deleteComments = comments.filter((item) => item.action === 'delete');
    deleteComments.forEach((item) => {
      const deleteIndex = unreadMsgCommentList.value.findIndex((unreadItem) => unreadItem.commentId === item.result.commentId);
      if (deleteIndex !== -1) {
        unreadMsgCommentList.value.splice(deleteIndex, 1);
      }
    });
  }
  //处理新增和修改的评论
  function handleMsgNewComments(comments: IssueCommentWSMessageResponse[]) {
    const newComments = comments.filter((item) => !['delete', 'update'].includes(item.action));
    const isRelatedToMeComments = newComments.filter((item) => isRelatedToMe(item.result));
    getUnreadMsgComment(isRelatedToMeComments.map(item => ({ commentId: item.result.commentId,
      createTime: item.result.createTime,
      updateTime: item.result.updateTime
    })));
  }

  const debounceQueryUnreadComment = debounce(async () => {
    const issueId = actionStore.action.value?.issueId;
    if (issueId) {
      const res = await queryUnReadComment(issueId);
      if (res) {
        getUnreadMsgComment(res, true);
        const { show, comment, comments } = await judgeShowBubbleByActionUnreadComment(res);
        if (show) {
          showBubble.show = true;
          showBubble.comment = comment;
          showBubble.comments = comments;
        }
      }
    }
  }, 200);

  //处理更新的评论（更新的评论有回复ok 回复赞 修改了评论内容等）
  function handleMsgUpdateComments(comments: IssueCommentWSMessageResponse[]) {
    const updateComments = comments.filter((item) => item.action === 'update');
    //调用接口获取
    if (updateComments.length) {
      debounceQueryUnreadComment();
    }
  }

  //ws推送评论消息action 为 update的情况尤其复杂  直接调用接口获取未读评论

  //读评论 分两种 一种是消息 一种是所有未读评论
  const readComment = (commentId: number) => {
    console.log('readComment', commentId);
    //1 将未读消息评论记录中的该评论移除
    const unreadMsgCommentIndex = unreadMsgCommentList.value.findIndex((item) => item.commentId === commentId);
    if (unreadMsgCommentIndex > -1) {
      unreadMsgCommentList.value.splice(unreadMsgCommentIndex, 1);
      const issueId = actionStore.action.value?.issueId;
      if (issueId && commentId) {
        markCommentIdMessageRead(commentId, issueId, 'comment');
      }
    }

    //2、把未读消息置为已读
    inProgressUnreadComment.value.push(commentId);
    setTimeout(() => {
      const unreadCommentIndex = unreadCommentList.value.findIndex((item) => item.commentId === commentId);
      if (unreadCommentIndex > -1) {
        unreadCommentList.value.splice(unreadCommentIndex, 1);
      }
      const inProgressIndex = inProgressUnreadComment.value.findIndex((item) => item === commentId);
      if (inProgressIndex > -1) {
        inProgressUnreadComment.value.splice(inProgressIndex, 1);
      }
    }, 3000);

  };

  //评论总结start

  //评论总结的前提 //1 评论条数大于5
  //       //2 评论的字数大于200

  const judgeShowAiSummarize = async () => {
    if (!compositionComments.value.length) {
      return false;
    }
    //出现总结按钮的条件
    //1 评论条数大于5
    //2 评论的字数大于200
    const allComments = compositionComments.value.flatMap((comment) => [comment, ...comment.subReplies]);

    const commentLength = allComments.filter((comment) => comment.deleteFlag === 0 && comment.plainContent).length;
    if (commentLength > 5) {
      return true;
    }
    await nextTick();
    //字符超过200个
    const allContent = allComments.reduce((pre: string, cur: any) => {
      return pre + (cur?.plainContent || '');
    }, '');
    return allContent.length > 200;
  };

  //判断评论是否符合展示引导总结的条件
  const singleCommentFitShowAiSummarize = async (commentId: number, self?: boolean) => {
    //self 为true时，表只判断自己的评论， 为false时代表判断自己 与 自己上面的评论
    await until(commentLoading).toBe(false);
    const { comment, upComments } = findCommentAndUpComments(commentId, self);
    if (upComments.filter(item => item.deleteFlag === 0 && item.plainContent).length > 5) {
      return {
        show: true,
        comment,
        comments: upComments
      };
    }
    //符合条件的评论的总字数超过200时
    const allContent = upComments.reduce((pre: string, cur: any) => {
      return pre + (cur?.plainContent || '');
    }, '');
    if (allContent.length > 200) {
      return {
        show: true,
        comment,
        comments: upComments
      };
    }
    return {
      show: false,
      comments: upComments
    };
  };
  // 从评论列表找到commentId对应的评论和这条评论上面的全部评论
  const findCommentAndUpComments = (commentId: number, self?: boolean) => {
    const allComments = compositionComments.value.flatMap((comment) => [comment, ...comment.subReplies]);
    //找到这条评论
    const comment = allComments.find((comment) => comment.commentId === commentId);
    if (!comment) {
      return {
        comment: {} as CommentIssue | IssueCommentReply,
        upComments: [] as (CommentIssue | IssueCommentReply)[]
      };
    }
    if (comment?.groupId) {
      //comment为子评论
      const parentComment = allComments.find((commentItem) => commentItem.commentId === comment.groupId) as CommentIssue;
      const subComment = parentComment?.subReplies || [];
      return {
        comment: comment,
        upComments: [parentComment, ...subComment]
      };
    }
    //comment为父评论
    const subComment = (comment as CommentIssue)?.subReplies || [];

    const upParentComments = compositionComments.value.filter((commentItem) => commentItem.createTime <= comment.createTime);
    const upComments = upParentComments.flatMap((comment) => [comment, ...comment.subReplies]);
    return {
      comment: comment as CommentIssue | IssueCommentReply,
      upComments: uniqWith([...(self ? [comment] : upComments), ...subComment ].filter(item => item), (a, b) => {
        return a.commentId === b.commentId;
      }) as (CommentIssue | IssueCommentReply)[]
    };
  };

  //评论总结分为静态处理 和 动态处理（ws）

  //静态处理分为两部分
  // 1、打开详情时， 接口返回了一些未读的评论，从时间靠后的评论判断这个评论是否符合展示引导气泡的条件
  // 2、打开详情时 actionStore 已有 commentId，此时判断这个评论是否符合展示引导气泡的条件


  const judgeShowBubbleByActionUnreadComment = async (unreadCommentItems?: unreadCommentItem[]) => {
    if (actionStore.action.value?.type === 'Edit') {
      await until(commentLoading).toBe(false);
      const commentId = actionStore.action.value?.commentId;
      const isRead = actionStore.action.value?.isRead;
      const allComments = compositionComments.value.flatMap((comment) => [comment, ...comment.subReplies]);
      const actionComment = (isRead || !isBoolean(isRead) || unreadCommentItems) ? undefined : allComments.find((commentItem) => commentItem.commentId === commentId);
      const unreadComments = unreadCommentItems ? unreadCommentItems : (issueStore.issueAssemble.value?.unreadMsgComments || []);
      //createTime倒序
      const allUnreadComment = sortBy([actionComment, ...unreadComments].filter((comment) => comment), 'createTime')?.reverse() || [];
      //将allUnreadComment遍历到singleCommentFitShowAiSummarize方法，当返回true 则停止执行
      for (let i = 0; i < allUnreadComment.length; i++) {
        const commentId = allUnreadComment[i]?.commentId;
        if (commentId) {
          // eslint-disable-next-line no-await-in-loop
          const { show, comment, comments } = await singleCommentFitShowAiSummarize(commentId);
          const commentAtInfos = comment?.commentAttributes?.commentAtInfos;
          //需要把自己发的消息排除掉 还有不是艾特我的消息
          if (show && commentAtInfos && commentAtInfos?.length) {
            const commentAtIds = commentAtInfos.map((item) => item.userId);
            if (commentAtIds.includes(vuex.state.user.account.userId) && comment.commentUser?.userId !== vuex.state.user.account.userId) {
              // eslint-disable-next-line no-await-in-loop
              const aiRight = await vuex.dispatch('orderRight/checkAiRight');
              if (!aiRight) {
                return {
                  show: false,
                  comment: undefined,
                  comments: []
                };
              }
              return {
                show,
                comment,
                comments
              };
            }
          }
        }
      }
    }
    return {
      show: false,
      comment: undefined,
      comments: []
    };
  };
  //动态处理只需要判断ws推送的消息是否符合展示引导气泡的条件
  const judgeNewWsShowBubble = async (comments: IssueCommentWSMessageResponse[]) => {
    const newComments = comments.filter((item) => !['delete', 'update'].includes(item.action));
    if (newComments?.length) {
      const wsComments = sortBy(newComments.map((item) => item.result), 'createTime')?.reverse() || [];
      const { show, comment, comments } = await singleCommentFitShowAiSummarize(wsComments[0].commentId);
      if (show) {
        const commentAtInfos = comment?.commentAttributes?.commentAtInfos;
        if (commentAtInfos && commentAtInfos?.length) {
          const commentAtIds = commentAtInfos.map((item) => item.userId);
          if (commentAtIds.includes(vuex.state.user.account.userId) && comment.commentUser?.userId !== vuex.state.user.account.userId) {
            const aiRight = await vuex.dispatch('orderRight/checkAiRight');
            if (aiRight) {
              showBubble.show = true;
              showBubble.comment = comment;
              showBubble.comments = comments;
            }
          }
        }
      }
    }
  };

  //重置showBubble
  const resetShowBubble = () => {
    showBubble.show = false;
    showBubble.comment = undefined;
    showBubble.comments = [];
  };

  //评论总结end


  // LSOTP-2118 未读评论之前是关于我的评论，这里要改成所有评论

  //b、所有评论的未读记录
  const unreadCommentList = ref<unreadCommentItem[]>([]);

  const lastViewCommentTime = ref<number>(0);
  //让后端记住时间戳，下次请求时，只返回这个时间戳之后的评论
  const recordLastViewTime = () => {
    const issueId = actionStore.action.value?.issueId;
    if (issueId) {
      //现在的时间戳
      const recordTime = Date.now();
      recordLastViewCommentTime(issueId, recordTime);
      lastViewCommentTime.value = recordTime;
    }
  };

  const handleUnreadComment = (comments: IssueCommentWSMessageResponse[]) => {
    handleDeleteUnreadComments(comments);
    handleUpdateUnreadComments(comments);
    handleNewUnreadComments(comments);
  };

  const handleDeleteUnreadComments = (comments: IssueCommentWSMessageResponse[]) => {
    const deleteComments = comments.filter((item) => item.action === 'delete');
    deleteComments.forEach((item) => {
      const deleteIndex = unreadCommentList.value.findIndex((unreadItem) => unreadItem.commentId === item.result.commentId);
      if (deleteIndex !== -1) {
        unreadCommentList.value.splice(deleteIndex, 1);
      }
    });
  };

  const handleUpdateUnreadComments = (comments: IssueCommentWSMessageResponse[]) => {
    const updateComments = comments.filter((item) => item.action === 'update');
    getUnreadComment(updateComments
      .filter(item => {
        return item.result.updateTime > lastViewCommentTime.value && item.result.commentUser.userId !== vuex.state.user.account.userId;
      })
      .map(item => ({ commentId: item.result.commentId,
        createTime: item.result.createTime,
        updateTime: item.result.updateTime
      })));
  };

  const handleNewUnreadComments = (comments: IssueCommentWSMessageResponse[]) => {
    const newComments = comments.filter((item) => !['delete', 'update'].includes(item.action));
    getUnreadComment(newComments
      .filter(item => {
        return item.result.commentUser.userId !== vuex.state.user.account.userId;
      })
      .map(item => ({ commentId: item.result.commentId,
        createTime: item.result.createTime,
        updateTime: item.result.updateTime
      })));
  };

  const getUnreadComment = (unreadComments: unreadCommentItem[], clear = false) => {
    if (clear) {
      unreadCommentList.value = [];
    }
    if (unreadComments?.length) {
      unreadComments.forEach((item: unreadCommentItem) => {
        const unreadCommentIds = unreadCommentList.value.map(
          (item) => item.commentId);
        //如果未读记录中没有该评论，则添加到未读记录中
        if (unreadCommentIds.includes(item.commentId)) {
          //先去除未读评论记录再添加
          unreadCommentList.value.splice(unreadCommentIds.indexOf(item.commentId), 1);
        }
        unreadCommentList.value.push(item);
      });
      unreadCommentList.value = [...unreadCommentList.value];
    }
  };

  //切到comment tab的时候记录时间戳
  layoutStore.onChangeRightTabEventOn(async (tab: string) => {
    if (tab === 'comment') {
      //等待issueAssemble有值
      await until(issueStore.isLoadComplete).toBe(true);
      recordLastViewTime();
    }
  });

  //当关闭详情的时候会记录时间戳
  const handleResetUnreadComment = () => {
    unreadCommentList.value = [];
    if (layoutStore.activeRightTab.value === 'comment') {
      recordLastViewTime();
      readInProgressUnreadComment();
    }
  };

  //当从comment切走的时候会记录时间戳
  watch(() => layoutStore.activeRightTab.value, (currentTab, lastTab) => {
    if (lastTab === 'comment') {
      recordLastViewTime();
      readInProgressUnreadComment();
    }
  });


  const highLightComments = ref<number[]>([]);
  //评论高亮封装，默认是点击事件监听时，取消高亮，也可以自定义方法取消高亮
  const setCommentHighLight = (commentIds: number[], customCancel?: Boolean) => {
    commentIds.forEach((id) => {
      if (highLightComments.value.includes(id)) {
        return;
      }
      return highLightComments.value.push(id);
    });
    const removeHighLightCommentId = () => {
      commentIds.forEach((commentId) => {
        const index = highLightComments.value.indexOf(commentId);
        if (index !== -1) {
          highLightComments.value.splice(index, 1);
        }
      });
      document.removeEventListener('click', removeHighLightCommentId);
    };
    if (!customCancel) {
      document.addEventListener('click', removeHighLightCommentId);
    }
    return {
      removeHighLightCommentId
    };
  };

  //将正在计时的未读评论全部置为已读
  const readInProgressUnreadComment = () => {
    inProgressUnreadComment.value.forEach((commentId) => {
      const unreadCommentIndex = unreadCommentList.value.findIndex((item) => item.commentId === commentId);
      if (unreadCommentIndex > -1) {
        unreadCommentList.value.splice(unreadCommentIndex, 1);
      }
    });
  };

  return {
    receiveCommentMessages,
    getRecommendedMembers,
    compositionComments,
    commentOrder: order,
    onCommentsReceive,
    refreshComments: refresh,
    onReceiveCommentLocalMessage,
    commentLoading,
    unreadMsgCommentList,
    unreadCommentList,
    readComment,
    localMessages,
    judgeShowAiSummarize,
    showBubble,
    singleCommentFitShowAiSummarize,
    resetShowBubble,
    highLightComments,
    setCommentHighLight,
    recordLastViewTime,
    handleResetUnreadComment
  };
};

export type CommentStore = ReturnType<typeof useComment>;
